import React, { useMemo, useState } from "react";
import AnalysisSnapshotContent from "../components/AnalysisSnapshotContent";
import useRadarScanner from "../hooks/useRadarScanner";
import { buildRadarTargets, RADAR_SYMBOLS } from "../services/radar/radarConfig";

const DEFAULT_FILTERS = {
  query: "",
  symbol: "ALL",
  market: "ALL",
  timeframe: "300",       // default: M5 only
  direction: "ALL",
  htfAlignment: "ALL",
  bosState: "ALL",
  zoneOutcome: "ALL",
};

function formatPriority(priority) {
  return String(priority || "WATCHLIST").replace("_", " ");
}

function matchesFilters(candidate, filters) {
  if (filters.symbol !== "ALL" && candidate.symbol !== filters.symbol) return false;
  if (filters.market !== "ALL" && candidate.market !== filters.market) return false;
  if (filters.timeframe !== "ALL" && String(candidate.timeframe) !== filters.timeframe) return false;
  if (filters.direction !== "ALL" && candidate.direction !== filters.direction) return false;

  if (filters.htfAlignment !== "ALL") {
    if (!candidate.htfAlignment) return false;
    if (candidate.htfAlignment !== filters.htfAlignment) return false;
  }

  if (filters.bosState !== "ALL") {
    if (candidate.panelState !== "ANALYZED") return false;
    const bos = candidate.bosLockState || "";
    if (filters.bosState === "LOCKED" && bos !== "LOCKED") return false;
    if (filters.bosState === "WAITING" && bos === "LOCKED") return false;
  }

  if (filters.zoneOutcome !== "ALL") {
    if (candidate.panelState !== "ANALYZED") return false;
    if ((candidate.zoneOutcomeState || "FRESH") !== filters.zoneOutcome) return false;
  }

  if (
    filters.query &&
    !`${candidate.symbol} ${candidate.lockedZoneLabel || ""} ${candidate.signalReason || ""}`
      .toLowerCase()
      .includes(filters.query.toLowerCase())
  ) {
    return false;
  }

  return true;
}

function sortPanels(panels, sortBy, sortDirection) {
  const dir = sortDirection === "asc" ? 1 : -1;

  return [...panels].sort((a, b) => {
    if (sortBy === "symbol") return a.symbol.localeCompare(b.symbol) * dir;
    if (sortBy === "timeframe") return (a.timeframe - b.timeframe) * dir;
    if (sortBy === "confidence") return ((a.confidence || 0) - (b.confidence || 0)) * dir;
    if (sortBy === "priority") {
      const w = (p) => (p === "HIGH_PRIORITY" ? 3 : p === "MEDIUM_PRIORITY" ? 2 : 1);
      return (w(a.priority) - w(b.priority)) * dir;
    }
    return ((a.score || 0) - (b.score || 0)) * dir;
  });
}

function getPriorityColor(priority) {
  if (priority === "HIGH_PRIORITY") return "#22c55e";
  if (priority === "MEDIUM_PRIORITY") return "#38bdf8";
  return "#94a3b8";
}

function getPanelStateBadge(panelState, signalState) {
  if (panelState === "RUNNING") return { label: "Scanning…", color: "#f59e0b" };
  if (panelState === "QUEUED")  return { label: "Queued",    color: "#64748b" };
  if (panelState === "FILTERED") return { label: "Filtered", color: "#64748b" };
  if (signalState === "SPIKE")  return { label: "Signal Ready", color: "#22c55e" };
  if (signalState === "ARMED")  return { label: "Armed",    color: "#38bdf8" };
  if (signalState === "WATCH")  return { label: "Watching", color: "#f59e0b" };
  if (signalState === "FAIL")   return { label: "Failed",   color: "#ef4444" };
  return { label: "Monitoring", color: "#94a3b8" };
}

function RadarPanelCard({ candidate, onOpenChart }) {
  const [collapsed, setCollapsed] = useState(false);
  const market = candidate.market || "BOOM";
  const stateBadge = getPanelStateBadge(candidate.panelState, candidate.signalState);
  const priorityColor = getPriorityColor(candidate.priority);

  return (
    <div style={panelStyles.root(candidate.panelState)}>
      <div style={panelStyles.header}>
        <div style={panelStyles.headerCopy}>
          <div style={panelStyles.kicker}>Market Monitor</div>
          <div style={panelStyles.title}>{candidate.symbol}</div>
          <div style={panelStyles.subtitle}>
            {candidate.timeframeLabel}&nbsp;|&nbsp;Score {candidate.score}&nbsp;|&nbsp;{candidate.confidence}% conf
          </div>
        </div>
        <div style={panelStyles.headerRight}>
          <div style={panelStyles.marketBadge(market)}>{market}</div>
          {candidate.htfAlignment && (
            <div style={{
              ...panelStyles.htfBadge,
              color: candidate.htfAlignment === "ALIGNED" ? "#22c55e" : "#f59e0b",
              borderColor: candidate.htfAlignment === "ALIGNED" ? "#22c55e66" : "#f59e0b66",
            }}>
              {candidate.htfAlignment === "ALIGNED" ? "HTF Aligned" : "HTF Optional"}
            </div>
          )}
          <div style={{ ...panelStyles.stateBadge, color: stateBadge.color, borderColor: stateBadge.color + "88" }}>
            {stateBadge.label}
          </div>
          <button type="button" style={panelStyles.collapseBtn} onClick={() => setCollapsed((c) => !c)}>
            {collapsed ? "+" : "−"}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div style={panelStyles.body}>
          {candidate.pipelineAnalysis ? (
            <AnalysisSnapshotContent analysis={candidate.pipelineAnalysis} compact />
          ) : (
            <div style={panelStyles.waiting}>
              <div style={panelStyles.waitingTitle}>
                {candidate.panelState === "FILTERED"
                  ? "Filtered — no signal"
                  : candidate.panelState === "RUNNING"
                  ? "Scan running…"
                  : "Waiting for scan"}
              </div>
              <div style={panelStyles.waitingReason}>
                {candidate.rejectionReason || candidate.queueState || "This pair will show the full workflow panel once the radar scan completes."}
              </div>
            </div>
          )}

          <div style={panelStyles.footer}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ ...panelStyles.pill, color: priorityColor, borderColor: priorityColor + "66" }}>
                {formatPriority(candidate.priority)}
              </span>
              {candidate.direction && candidate.direction !== "--" && (
                <span style={panelStyles.footerMeta}>{candidate.direction}</span>
              )}
              {candidate.lockedZoneLabel && candidate.lockedZoneLabel !== "--" && (
                <span style={panelStyles.footerMeta}>{candidate.lockedZoneLabel}</span>
              )}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={panelStyles.footerTime}>{candidate.lastUpdateLabel}</span>
              <button
                type="button"
                style={panelStyles.chartBtn}
                onClick={() => onOpenChart(candidate.symbol, candidate.timeframe)}
              >
                Open Chart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── unique sorted symbol list for the pairs filter ─────────────────────────
const ALL_SYMBOLS = [...new Set(RADAR_SYMBOLS)].sort();

export default function RadarDashboard({
  user,
  onLogout,
  onNavigateLive,
  onNavigateResults,
  onNavigateRadar,
  onNavigateAutoTrader,
  onOpenChart,
}) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sort, setSort] = useState({ by: "score", direction: "desc" });

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const resetFilters = () => setFilters(DEFAULT_FILTERS);
  const isFiltered = Object.entries(filters).some(([k, v]) => v !== DEFAULT_FILTERS[k]);
  const activeFilterStyle = (key) =>
    filters[key] !== DEFAULT_FILTERS[key] ? fbStyles.active : undefined;

  const {
    candidates,
    rejected,
    upcoming,
    metrics,
    scanning,
    paused,
    logs,
    refreshNow,
    pauseScanning,
    resumeScanning,
  } = useRadarScanner({
    user,
    enabled: true,
    sortBy: sort.by,
    sortDirection: sort.direction,
  });

  const pairPanels = useMemo(() => {
    const allTargets = buildRadarTargets();
    const candidateMap = new Map(candidates.map((c) => [c.id, c]));
    const rejectedMap  = new Map(rejected.map((r) => [r.id, r]));
    const upcomingMap  = new Map(upcoming.map((u) => [u.id, u]));

    return sortPanels(
      allTargets.map((target) => {
        const candidate    = candidateMap.get(target.id);
        const rejectedItem = rejectedMap.get(target.id);
        const queueItem    = upcomingMap.get(target.id);

        if (candidate) return { ...candidate, panelState: "ANALYZED" };

        if (rejectedItem) {
          return {
            ...target,
            priority: "WATCHLIST",
            direction: "--",
            readiness: "FILTERED",
            signalState: "WAIT",
            score: 0, confidence: 0,
            lockedZoneLabel: "--",
            lastUpdateLabel: "Rejected",
            rejectionReason: rejectedItem.reason,
            hotness: rejectedItem.hotness,
            panelState: "FILTERED",
            pipelineAnalysis: null,
          };
        }

        const queued = Boolean(queueItem?.queued);
        return {
          ...target,
          priority: "WATCHLIST",
          direction: "--",
          readiness: "SCANNING",
          signalState: queued ? "RUNNING" : "WAIT",
          score: 0, confidence: 0,
          lockedZoneLabel: "--",
          lastUpdateLabel: queued ? "Scanning now" : "Queued",
          queueState: queued ? "Scan running now" : "Waiting for scan",
          hotness: queueItem?.hotness || target.hotness,
          panelState: queued ? "RUNNING" : "QUEUED",
          pipelineAnalysis: null,
        };
      }),
      sort.by,
      sort.direction
    );
  }, [candidates, rejected, sort.by, sort.direction, upcoming]);

  const filteredPanels = useMemo(
    () => pairPanels.filter((c) => matchesFilters(c, filters)),
    [filters, pairPanels]
  );

  const analyzedCount = pairPanels.filter((p) => p.panelState === "ANALYZED").length;

  return (
    <div className="radar-dashboard-shell">

      {/* ── Toolbar ── */}
      <div className="chart-toolbar" style={{ position: "sticky", top: 0, zIndex: 100 }}>
        <div className="dashboard-route-nav dashboard-route-nav-toolbar">
          <button type="button" className="dashboard-route-button" onClick={onNavigateLive}>
            Live Workspace
          </button>
          <button type="button" className="dashboard-route-button" onClick={onNavigateResults}>
            Saved Results
          </button>
          <button type="button" className="dashboard-route-button dashboard-route-button-active" onClick={onNavigateRadar}>
            Radar
          </button>
          <button type="button" className="dashboard-route-button" onClick={onNavigateAutoTrader}>
            Auto Trader
          </button>
        </div>

        {/* compact inline status + stats */}
        <div className="chart-toolbar-controls" style={{ flex: 1, gap: 10, minWidth: 0 }}>
          <span
            style={{
              width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
              background: scanning ? "#f59e0b" : paused ? "#64748b" : "#22c55e",
              boxShadow: scanning ? "0 0 6px #f59e0b" : paused ? "none" : "0 0 6px #22c55e",
            }}
          />
          <span className="chart-status-text" style={{ color: scanning ? "#f59e0b" : paused ? "#64748b" : "#22c55e", whiteSpace: "nowrap" }}>
            {scanning ? `Scanning ${metrics.activeScanCount}` : paused ? "Paused" : "Idle"}
          </span>

          <span style={tbStyles.divider} />

          {/* inline stats */}
          <span style={tbStyles.statsRow}>
            <strong style={{ color: "#e2e8f0" }}>{analyzedCount}</strong>
            <span style={tbStyles.statsLabel}>analyzed</span>
            <span style={tbStyles.dot}>·</span>
            <strong style={{ color: metrics.highPriorityCount > 0 ? "#22c55e" : "#e2e8f0" }}>{metrics.highPriorityCount}</strong>
            <span style={tbStyles.statsLabel}>high</span>
            <span style={tbStyles.dot}>·</span>
            <strong style={{ color: metrics.readyCount > 0 ? "#38bdf8" : "#e2e8f0" }}>{metrics.readyCount}</strong>
            <span style={tbStyles.statsLabel}>ready</span>
            <span style={tbStyles.dot}>·</span>
            <strong style={{ color: "#e2e8f0" }}>{metrics.rejectedCount}</strong>
            <span style={tbStyles.statsLabel}>rej</span>
            <span style={tbStyles.dot}>·</span>
            <span style={{ color: metrics.socketConnected ? "#22c55e" : "#ef4444", fontWeight: 700, fontSize: 11 }}>
              {metrics.socketConnected ? "● Live" : "○ Off"}
            </span>
          </span>

          <span style={tbStyles.divider} />

          <button
            type="button"
            className="toolbar-setting-button toolbar-setting-button-actionable"
            onClick={refreshNow}
            disabled={scanning}
            style={{ opacity: scanning ? 0.45 : 1 }}
          >
            Refresh
          </button>
          <button
            type="button"
            className="toolbar-setting-button toolbar-setting-button-actionable"
            onClick={paused ? resumeScanning : pauseScanning}
          >
            {paused ? "Resume" : "Pause"}
          </button>

          {logs.length > 0 && (
            <span style={tbStyles.logLine}>{logs[0].message}</span>
          )}
        </div>

        <div className="chart-toolbar-side">
          <div className="toolbar-session">
            <div className="toolbar-session-copy">
              <span className="toolbar-session-kicker">Signed In</span>
              <strong className="toolbar-session-name">
                {user?.full_name || user?.email || "Spike User"}
              </strong>
            </div>
            <button type="button" className="toolbar-session-logout" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* ── Filter bar — single scrollable line ── */}
      <div
        className="radar-filter-bar-line"
        style={{ padding: "7px 0", borderBottom: "1px solid rgba(57,74,104,0.35)", marginBottom: 12 }}
      >
        <input
          className="results-filter-input radar-fb-search"
          placeholder="Search…"
          value={filters.query}
          onChange={(e) => setFilter("query", e.target.value)}
          style={activeFilterStyle("query")}
        />

        <select
          className="results-filter-input"
          value={filters.symbol}
          onChange={(e) => setFilter("symbol", e.target.value)}
          style={activeFilterStyle("symbol")}
        >
          <option value="ALL">All Pairs</option>
          {ALL_SYMBOLS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <select
          className="results-filter-input"
          value={filters.market}
          onChange={(e) => setFilter("market", e.target.value)}
          style={activeFilterStyle("market")}
        >
          <option value="ALL">All Markets</option>
          <option value="BOOM">Boom</option>
          <option value="CRASH">Crash</option>
        </select>

        <select
          className="results-filter-input"
          value={filters.timeframe}
          onChange={(e) => setFilter("timeframe", e.target.value)}
          style={activeFilterStyle("timeframe")}
        >
          <option value="ALL">All TF</option>
          <option value="180">M3</option>
          <option value="300">M5</option>
          <option value="900">M15</option>
        </select>

        <span style={fbStyles.sep} />

        <select
          className="results-filter-input"
          value={filters.direction}
          onChange={(e) => setFilter("direction", e.target.value)}
          style={activeFilterStyle("direction")}
        >
          <option value="ALL">Direction</option>
          <option value="BULLISH">Bullish</option>
          <option value="BEARISH">Bearish</option>
        </select>

        <select
          className="results-filter-input"
          value={filters.htfAlignment}
          onChange={(e) => setFilter("htfAlignment", e.target.value)}
          style={activeFilterStyle("htfAlignment")}
        >
          <option value="ALL">HTF Align</option>
          <option value="ALIGNED">Aligned ✓</option>
          <option value="CONFLICT">Optional ⚠</option>
        </select>

        <select
          className="results-filter-input"
          value={filters.bosState}
          onChange={(e) => setFilter("bosState", e.target.value)}
          style={activeFilterStyle("bosState")}
        >
          <option value="ALL">BOS</option>
          <option value="LOCKED">Locked</option>
          <option value="WAITING">Waiting</option>
        </select>

        <select
          className="results-filter-input"
          value={filters.zoneOutcome}
          onChange={(e) => setFilter("zoneOutcome", e.target.value)}
          style={activeFilterStyle("zoneOutcome")}
        >
          <option value="ALL">Zone Outcome</option>
          <option value="APPROACHING">Approaching Zone</option>
          <option value="ACTIVE">Active Zone</option>
        </select>

        <span style={fbStyles.sep} />

        <select
          className="results-filter-input"
          value={`${sort.by}:${sort.direction}`}
          onChange={(e) => { const [by, direction] = e.target.value.split(":"); setSort({ by, direction }); }}
        >
          <option value="score:desc">Score ↓</option>
          <option value="confidence:desc">Conf ↓</option>
          <option value="priority:desc">Priority ↓</option>
          <option value="symbol:asc">A–Z</option>
          <option value="timeframe:asc">TF ↑</option>
        </select>

        <span style={fbStyles.count}>{filteredPanels.length}/{pairPanels.length}</span>

        <button
          type="button"
          className="toolbar-setting-button toolbar-setting-button-actionable"
          onClick={resetFilters}
          style={{ flexShrink: 0, opacity: isFiltered ? 1 : 0.3, pointerEvents: isFiltered ? "auto" : "none" }}
        >
          Reset
        </button>
      </div>

      {/* ── Panels grid ── */}
      {filteredPanels.length > 0 ? (
        <div style={gridStyles.grid}>
          {filteredPanels.map((candidate) => (
            <RadarPanelCard
              key={candidate.id}
              candidate={candidate}
              onOpenChart={onOpenChart}
            />
          ))}
        </div>
      ) : (
        <div className="results-chart-empty" style={{ marginTop: 32 }}>
          No panels match the current filters.&nbsp;
          <button
            type="button"
            className="toolbar-setting-button toolbar-setting-button-actionable"
            onClick={resetFilters}
          >
            Reset Filters
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Stat chip — kept for possible reuse but not used in toolbar ─────────────
function StatChip({ label, value, highlight = null, borderColor }) {  // eslint-disable-line no-unused-vars
  return (
    <span style={{
      display: "inline-flex", flexDirection: "column", gap: 1,
      padding: "3px 8px", borderRadius: 8,
      border: `1px solid ${borderColor || "rgba(71,85,105,0.5)"}`,
      background: "rgba(2,6,23,0.3)", minWidth: 32,
    }}>
      <span style={{ fontSize: 9, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</span>
      <strong style={{ fontSize: 12, fontWeight: 800, color: highlight || "#e2e8f0", lineHeight: 1.1 }}>{value}</strong>
    </span>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────
const panelStyles = {
  root: (panelState) => ({
    borderRadius: 16,
    border: `1px solid ${
      panelState === "ANALYZED" ? "rgba(74,115,170,0.5)"
      : panelState === "RUNNING" ? "rgba(245,158,11,0.4)"
      : "rgba(74,115,170,0.2)"
    }`,
    background: "linear-gradient(180deg, #091225 0%, #111b31 100%)",
    color: "#e5e7eb",
    boxShadow: "0 8px 24px rgba(2,6,23,0.38)",
    fontFamily: "'IBM Plex Sans','Segoe UI',Tahoma,sans-serif",
    animation: "panelFloatIn 220ms ease-out",
    display: "flex",
    flexDirection: "column",
  }),
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 6,
    padding: "10px 12px 8px",
    borderBottom: "1px solid rgba(63,83,120,0.5)",
  },
  headerCopy: { display: "grid", gap: 2, minWidth: 0 },
  kicker: {
    fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase",
    color: "#48b7ff", fontWeight: 800,
  },
  title: { fontSize: 14, fontWeight: 800, color: "#ffffff", lineHeight: 1.1 },
  subtitle: {
    fontSize: 9, letterSpacing: "0.04em",
    color: "#c7d3e7", textTransform: "uppercase",
  },
  headerRight: { display: "flex", alignItems: "center", gap: 5, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" },
  marketBadge: (market) => ({
    height: 20, padding: "0 7px", borderRadius: 999,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    background: market === "BOOM" ? "rgba(18,90,54,0.3)" : "rgba(96,32,46,0.28)",
    border: market === "BOOM" ? "1px solid rgba(49,196,114,0.45)" : "1px solid rgba(210,79,98,0.45)",
    color: market === "BOOM" ? "#a7f3c4" : "#ff8ca0",
    fontSize: 9, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase",
  }),
  htfBadge: {
    fontSize: 8, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase",
    padding: "2px 6px", borderRadius: 999, border: "1px solid", whiteSpace: "nowrap",
  },
  stateBadge: {
    fontSize: 8, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase",
    padding: "2px 6px", borderRadius: 999, border: "1px solid", whiteSpace: "nowrap",
  },
  collapseBtn: {
    minWidth: 20, height: 20, borderRadius: 999,
    border: "1px solid rgba(82,105,143,0.58)",
    background: "rgba(16,26,45,0.92)", color: "#d8e4f8",
    cursor: "pointer", fontSize: 12, fontWeight: 800, lineHeight: 1,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  body: { padding: "8px 10px 10px", display: "grid", gap: 6, flex: 1 },
  waiting: {
    display: "grid", gap: 4, padding: "10px 8px",
    borderRadius: 10, border: "1px solid rgba(57,74,104,0.5)",
    background: "rgba(17,27,49,0.6)",
  },
  waitingTitle: { color: "#cbd5e1", fontSize: 11, fontWeight: 700 },
  waitingReason: { color: "#64748b", fontSize: 10, lineHeight: 1.4 },
  footer: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    gap: 8, paddingTop: 6, borderTop: "1px solid rgba(57,74,104,0.4)", flexWrap: "wrap",
  },
  pill: {
    fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em",
    padding: "2px 7px", borderRadius: 999, border: "1px solid",
  },
  footerMeta: { color: "#64748b", fontSize: 10 },
  footerTime: { color: "#475569", fontSize: 9 },
  chartBtn: {
    padding: "4px 10px", borderRadius: 8,
    border: "1px solid rgba(82,105,143,0.6)",
    background: "rgba(16,26,45,0.9)", color: "#93c5fd",
    fontSize: 10, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
  },
};

/* toolbar compact inline styles */
const tbStyles = {
  divider: {
    width: 1, height: 16, background: "rgba(71,85,105,0.5)", flexShrink: 0, alignSelf: "center",
  },
  statsRow: {
    display: "flex", alignItems: "center", gap: 5, fontSize: 12, whiteSpace: "nowrap",
  },
  statsLabel: {
    fontSize: 10, color: "#64748b", marginLeft: 2,
  },
  dot: {
    color: "#334155", fontSize: 10, margin: "0 2px",
  },
  logLine: {
    fontSize: 10, color: "#475569", overflow: "hidden",
    textOverflow: "ellipsis", maxWidth: 160, whiteSpace: "nowrap",
  },
};

/* filter bar */
const fbStyles = {
  shell: {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    gap: 5,
    padding: "7px 0",
    borderBottom: "1px solid rgba(57,74,104,0.35)",
    marginBottom: 12,
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  sep: {
    width: 1, height: 18, background: "rgba(71,85,105,0.4)", flexShrink: 0,
  },
  active: {
    borderColor: "#38bdf8",
    boxShadow: "0 0 0 1px rgba(56,189,248,0.45)",
    background: "rgba(56,189,248,0.14)",
    color: "#e0f2fe",
  },
  // overrides results-filter-input's width:100% so each control stays intrinsic width
  fi: (variant) => ({
    width: "auto",
    flexShrink: 0,
    minHeight: 30,
    padding: "0 8px",
    fontSize: 11,
    ...(variant === "search" ? { minWidth: 110, flexShrink: 1 } : {}),
  }),
  count: {
    fontSize: 11, color: "#475569", whiteSpace: "nowrap", flexShrink: 0,
  },
};

const gridStyles = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 12,
    paddingBottom: 24,
  },
};
